/**
 * Stable photo URLs.
 *
 * Photo buckets are PUBLIC, so every object has one permanent, cacheable URL.
 * This is what the native WebView needs: re-entering an appraisal re-uses the
 * cached image instantly instead of re-signing (which produced a new URL each
 * mount and caused the "Loading photo…" flash / blank tiles).
 */

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL as string;

export function buildPhotoUrl(bucket: string, path: string): string {
  if (!path) return path;
  const encoded = path
    .split("/")
    .map((seg) => encodeURIComponent(seg))
    .join("/");
  return `${SUPABASE_URL}/storage/v1/object/public/${bucket}/${encoded}`;
}

/**
 * True when the URL is already a stable, directly-renderable public URL.
 * Legacy photo-proxy URLs deliberately return false so callers rebuild them
 * into the canonical public form.
 */
export function isPhotoProxyUrl(url: string): boolean {
  return !!url && url.includes("/storage/v1/object/public/");
}

/** Extracts a storage path out of any legacy proxy URL form. */
export function extractPathFromProxyUrl(url: string): { bucket: string; path: string } | null {
  if (!url || !url.includes("/functions/v1/photo-proxy")) return null;
  try {
    const u = new URL(url);
    const b = u.searchParams.get("b");
    const p = u.searchParams.get("p");
    if (b && p) return { bucket: b, path: p };
    const marker = "/functions/v1/photo-proxy/";
    const idx = url.indexOf(marker);
    if (idx !== -1) {
      const rest = decodeURIComponent(url.substring(idx + marker.length).split("?")[0]);
      const slash = rest.indexOf("/");
      if (slash > 0) return { bucket: rest.substring(0, slash), path: rest.substring(slash + 1) };
    }
  } catch {
    /* ignore */
  }
  return null;
}
